import { ReconnectOptions, WebSocketEventHandlers } from './types.js';

const DEFAULT_MAX_ATTEMPTS = 3; // Changed from 0 to 3
const CONNECTION_TIMEOUT_MS = 10000; // 10 seconds

enum ConnectionState {
  DISCONNECTED = 'DISCONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  RECONNECTING = 'RECONNECTING',
  ERROR = 'ERROR'
}

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private isIntentionallyClosed = false;
  private reconnectAttempts = 0;
  private maxAttempts = DEFAULT_MAX_ATTEMPTS;
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;

  get connected(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN && this.connectionState === ConnectionState.CONNECTED;
  }

  get state(): string {
    return this.connectionState;
  }

  configureReconnect(options?: ReconnectOptions): void {
    this.maxAttempts = options?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  }

  connect(url: string, handlers: WebSocketEventHandlers = {}): Promise<boolean> {
    // Cancel any pending automatic reconnect – an explicit connect() takes precedence
    this.clearReconnectTimeout();

    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      // Scenario: Attempt to connect while connection is already in progress
      return Promise.resolve(false);
    }

    // Close any existing WebSocket before creating a new one
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      this.ws.close();
      this.ws = null;
    }

    this.isIntentionallyClosed = false;
    this.isConnecting = true;

    // Only reset attempts on fresh connection (not reconnection)
    if (this.connectionState === ConnectionState.DISCONNECTED) {
      this.reconnectAttempts = 0;
    }

    this.connectionState = this.reconnectAttempts > 0 ? ConnectionState.RECONNECTING : ConnectionState.CONNECTING;

    return new Promise((resolve, reject) => {
      try {
        // Connection timeout
        this.connectionTimeout = globalThis.setTimeout(() => {
          if (this.connectionState === ConnectionState.CONNECTING || this.connectionState === ConnectionState.RECONNECTING) {
            // Scenario: Timeout connecting to WebSocket (server did not respond in 10s)
            this.cleanup();
            this.connectionState = ConnectionState.ERROR;
            this.isConnecting = false;
            reject(new Error('Connection timeout'));
          }
        }, CONNECTION_TIMEOUT_MS);

        this.ws = new WebSocket(url);
        let settled = false;

        this.ws.onopen = () => {
          this.clearConnectionTimeout();
          this.reconnectAttempts = 0;
          this.connectionState = ConnectionState.CONNECTED;
          this.isConnecting = false;
          handlers.onOpen?.();
          if (!settled) {
            settled = true;
            resolve(true);
          }
        };

        this.ws.onmessage = event => {
          handlers.onMessage?.(event);
        };

        this.ws.onerror = event => {
          this.clearConnectionTimeout();
          this.connectionState = ConnectionState.ERROR;
          // Scenario: WebSocket error (network failed, server rejected, DNS failed, etc)
          handlers.onError?.(event);
          if (!settled) {
            settled = true;
            this.isConnecting = false;
            reject(new Error('WebSocket connection error'));
          }
        };

        this.ws.onclose = event => {
          this.clearConnectionTimeout();
          const wasConnected = this.connectionState === ConnectionState.CONNECTED;
          this.connectionState = ConnectionState.DISCONNECTED;
          // Scenario: WebSocket closed (server disconnected, network lost, timeout, etc)
          handlers.onClose?.(event);

          if (!settled) {
            settled = true;
            this.isConnecting = false;
            reject(new Error('WebSocket closed'));
          } else if (!this.isIntentionallyClosed && wasConnected) {
            // Scenario: Connection lost after being connected - keep reconnect user-driven
          }
        };
      } catch (error) {
        this.clearConnectionTimeout();
        this.connectionState = ConnectionState.ERROR;
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  send(message: string): void {
    if (!this.ws || !this.connected) {
      throw new Error('WebSocket not connected');
    }
    try {
      this.ws.send(message);
    } catch (error) {
      throw new Error('Failed to send message');
    }
  }

  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.isConnecting = false;
    this.connectionState = ConnectionState.DISCONNECTED;
    this.clearReconnectTimeout();
    this.cleanup();
  }

  /** Stop reconnection attempts (e.g., on 403/401 errors where retry won't help) */
  stopReconnection(): void {
    this.isIntentionallyClosed = true;
    this.reconnectAttempts = this.maxAttempts;
  }

  get intentionallyClosed(): boolean {
    return this.isIntentionallyClosed;
  }

  /** Pause reconnection attempts while the browser is offline. */
  pauseReconnection(): void {
    this.clearReconnectTimeout();
  }

  /** Resume reconnection tracking after the browser comes back online. */
  resumeReconnection(): void {
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private cleanup(): void {
    this.clearConnectionTimeout();
    this.clearReconnectTimeout();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

export const websocketManager = new WebSocketManager();
export default WebSocketManager;
